import { useMemo, useState } from "react"
import { Link } from "@tanstack/react-router"
import { IconEnvelopeFill18 } from "nucleo-ui-fill-18"
import {
	IconChevronRightOutline18 as ChevronRight,
	IconArrowDownToLineOutline18 as DownloadIcon,
	IconCircleInfoOutline18 as InfoIcon,
	IconPlusOutline18 as PlusIcon,
} from "nucleo-ui-outline-18"

import type { EmailSettings } from "@productlane/zero"
import { domainOfEmail } from "@productlane/lib/src/email-domain"
import { verifiedDomains } from "@productlane/lib/src/email-settings"
import {
	Badge,
	Button,
	CopyButton,
	Dialog,
	Input,
	Switch,
} from "@productlane/ui"

import type { EmailInboxSettingsProps } from "./types"
import { toast } from "@/components/toast"
import { api } from "@/lib/api"
import { SettingsContainer } from "../settings-switches/settings-container"
import { DnsRecordBlock } from "./dns-record-block"
import { downloadDnsRecords } from "./download-dns-records"

function AddressRow({ address }: { address: EmailSettings }) {
	const isDnsVerified = address.isTxtDnsActive && address.isCnameDnsActive

	return (
		<Link
			to="/settings/emails/addresses/$addressId"
			params={{ addressId: address.id }}
			className="flex items-center gap-2 rounded-lg border border-gray-200 px-3 py-2.5 hover:bg-gray-150 dark:border-gray-700/80 dark:hover:bg-gray-800"
		>
			<IconEnvelopeFill18 className="size-4 flex-none text-tertiary" />
			<span className="truncate text-sm">{address.primaryEmail}</span>
			{address.isPrimary && <Badge variant="default">Primary</Badge>}
			{address.isBroadcastDefault && (
				<Badge variant="default">Broadcasts</Badge>
			)}
			{!isDnsVerified && <Badge variant="important">DNS pending</Badge>}
			{isDnsVerified && !address.isForwardingSetup && (
				<Badge variant="important">Forwarding pending</Badge>
			)}
			<span className="flex-1" />
			<ChevronRight className="size-3 flex-none text-tertiary" />
		</Link>
	)
}

const FORWARDING_DOCS_ARTICLE = "6c07a98b-6728-4f77-b0a8-4ce1525175ba"

type DialogStep = "input" | "forward" | "dns"

function AddAddressDialog({
	isOpen,
	onOpenChange,
	addresses,
	limit,
	used,
}: {
	isOpen: boolean
	onOpenChange: (open: boolean) => void
	addresses: readonly EmailSettings[]
	limit: number
	used: number
}) {
	const utils = api.useUtils()
	const [email, setEmail] = useState("")
	const [step, setStep] = useState<DialogStep>("input")
	const [created, setCreated] = useState<EmailSettings | null>(null)
	const [forwardingConfirmed, setForwardingConfirmed] = useState(false)

	const domains = verifiedDomains(addresses)

	// Completing the typed local part on every verified domain keeps the fast
	// path one Enter away; anything else is the new-domain path.
	const suggestions = useMemo(() => {
		const value = email.trim().toLowerCase()
		if (!value) return []
		const atIndex = value.indexOf("@")
		const localPart = (atIndex === -1 ? value : value.slice(0, atIndex)) || ""
		const typedDomain = atIndex === -1 ? "" : value.slice(atIndex + 1)
		if (!localPart) return []
		const taken = new Set(addresses.map((a) => a.primaryEmail))
		const verified = domains
			.filter((domain) => !typedDomain || domain.startsWith(typedDomain))
			.map((domain) => ({
				email: `${localPart}@${domain}`,
				hint: "Verified domain, live instantly",
			}))
			.filter((s) => !taken.has(s.email))
		const isNewDomain =
			typedDomain.includes(".") && !domains.includes(typedDomain)
		const custom = isNewDomain
			? [
					{
						email: `${localPart}@${typedDomain}`,
						hint: "New domain, 2 DNS records to add",
					},
				]
			: []
		return [...verified, ...custom].slice(0, 5)
	}, [email, addresses, domains])

	const createMutation = api.mail.createEmailAddress.useMutation({
		onSuccess: async (row) => {
			await Promise.all([
				utils.mail.emailAddressLimit.invalidate(),
				utils.workspace.info.invalidate(),
			])
			setCreated(row as unknown as EmailSettings)
			setStep(row.isEnabled ? "forward" : "dns")
		},
		onError: (error) => {
			toast.error({
				title: "Could not add the address",
				description:
					error.data?.code === "INTERNAL_SERVER_ERROR" || !error.message
						? "Something went wrong on our side. Try again, or contact us if it keeps failing."
						: error.message,
			})
		},
	})

	const verifyMutation = api.mail.verifyEmailDomain.useMutation({
		onSuccess: async () => {
			await utils.workspace.info.invalidate()
			toast.success({
				title: `${created?.domain} verified`,
				description: "The address is live. Set up forwarding to receive mail.",
				icon: IconEnvelopeFill18,
			})
			onOpenChange(false)
		},
		onError: (error) => {
			toast.error({ title: "DNS is not live yet", description: error.message })
		},
	})

	const forwardingMutation = api.mail.updateEmailAddress.useMutation({
		onSuccess: async () => {
			await utils.workspace.info.invalidate()
			onOpenChange(false)
		},
		onError: (error) => {
			toast.error({
				title: "Could not save the setting",
				description: error.message,
			})
		},
	})

	const handleOpenChange = (open: boolean) => {
		onOpenChange(open)
		if (!open) {
			setEmail("")
			setStep("input")
			setCreated(null)
			setForwardingConfirmed(false)
		}
	}

	const handleAdd = (value?: string) => {
		const address = (value ?? email).trim().toLowerCase()
		if (used >= limit) {
			toast.info({
				title: `Your plan includes ${limit} email ${limit === 1 ? "address" : "addresses"}`,
				description: "Upgrade to add more.",
				icon: IconEnvelopeFill18,
			})
			return
		}
		if (!address.includes("@") || !domainOfEmail(address)?.includes(".")) {
			toast.info({
				title: "Enter the full address",
				description: `Something like sales@${domains[0] ?? "acme.com"}.`,
				icon: IconEnvelopeFill18,
			})
			return
		}
		setEmail(address)
		createMutation.mutate({ email: address })
	}

	return (
		<Dialog
			isOpen={isOpen}
			onOpenChange={handleOpenChange}
			title="Add address"
			size="lg"
		>
			{step === "input" && (
				<div className="flex flex-col gap-3">
					<p className="text-sm text-secondary">
						Type the full address. Domains you already verified go live
						instantly; a new domain gets two DNS records first.
					</p>
					<Input
						name="newAddress"
						value={email}
						placeholder={`sales@${domains[0] ?? "acme.com"}`}
						autoFocus
						autoComplete="off"
						spellCheck={false}
						onChange={(e) => setEmail(e.target.value)}
						onKeyDown={(e) => {
							if (e.key === "Enter") handleAdd(suggestions[0]?.email)
						}}
					/>
					{suggestions.length > 0 && (
						<div className="flex flex-col rounded-lg border border-gray-200 p-1 dark:border-gray-700/80">
							{suggestions.map((suggestion) => (
								<button
									key={suggestion.email}
									type="button"
									className="flex cursor-default items-center justify-between gap-2 rounded-md px-2 py-1.5 text-left text-sm font-medium hover:bg-gray-100 dark:hover:bg-gray-800"
									onClick={() => handleAdd(suggestion.email)}
								>
									<span className="truncate">{suggestion.email}</span>
									<span className="flex-none text-[11px] text-tertiary">
										{suggestion.hint}
									</span>
								</button>
							))}
						</div>
					)}
					<div className="flex justify-end gap-2 pt-2">
						<Button variant="ghost" onClick={() => handleOpenChange(false)}>
							Cancel
						</Button>
						<Button
							variant="default"
							isLoading={createMutation.isLoading}
							onClick={() => handleAdd(suggestions[0]?.email)}
						>
							Add address
						</Button>
					</div>
				</div>
			)}

			{step === "forward" && created && (
				<div className="flex flex-col gap-3">
					<p className="text-sm text-secondary">
						{created.primaryEmail} is live. One step left: forward its inbound
						mail to Productlane.
					</p>
					<Button
						Icon={InfoIcon}
						variant="link"
						className="self-start"
						onClick={() => {
							// @ts-expect-error Global widget calls
							// eslint-disable-next-line
							window.Productlane.openDocs(FORWARDING_DOCS_ARTICLE)
						}}
					>
						How to set up forwarding
					</Button>
					<div className="flex items-center gap-2 rounded-lg bg-gray-100 p-3 dark:bg-gray-800">
						<span className="min-w-0 flex-1 truncate font-mono text-xs">
							{created.brandedInboundEmailAddress}
						</span>
						<CopyButton
							value={created.brandedInboundEmailAddress ?? ""}
							className="!text-tertiary hover:!text-primary"
						/>
					</div>
					<div className="flex items-center justify-between gap-2">
						<span className="text-xs font-medium text-secondary">
							I have added forwarding
						</span>
						<Switch
							name="forwarding-confirmed"
							checked={forwardingConfirmed}
							onCheckedChange={setForwardingConfirmed}
						/>
					</div>
					<div className="flex justify-end gap-2 pt-2">
						<Button variant="ghost" onClick={() => handleOpenChange(false)}>
							Do it later
						</Button>
						<Button
							variant="default"
							disabled={!forwardingConfirmed}
							isLoading={forwardingMutation.isLoading}
							onClick={() =>
								forwardingMutation.mutate({
									id: created.id,
									isForwardingSetup: true,
								})
							}
						>
							Done
						</Button>
					</div>
				</div>
			)}

			{step === "dns" && created && (
				<div className="flex flex-col gap-3">
					<p className="text-sm text-secondary">
						{created.primaryEmail} was created. Add these records at your DNS
						provider; the address stays inactive until {created.domain} is
						verified.
					</p>
					<div>
						<DnsRecordBlock
							type="TXT"
							recordKey={created.txtDnsKey}
							value={created.txtDnsValue}
							isActive={created.isTxtDnsActive}
						/>
						<DnsRecordBlock
							type="CNAME"
							recordKey={created.cnameDnsKey}
							value={created.cnameDnsValue}
							isActive={created.isCnameDnsActive}
						/>
					</div>
					<div className="flex items-center gap-2 pt-2">
						<Button
							variant="outline"
							Icon={DownloadIcon}
							onClick={() => downloadDnsRecords(created)}
						>
							Download for Cloudflare
						</Button>
						<span className="flex-1" />
						<Button variant="ghost" onClick={() => handleOpenChange(false)}>
							Verify later
						</Button>
						<Button
							variant="default"
							isLoading={verifyMutation.isLoading}
							onClick={() => verifyMutation.mutate({ id: created.id })}
						>
							Verify DNS records
						</Button>
					</div>
				</div>
			)}
		</Dialog>
	)
}

export function SupportAddressesCard({ workspace }: EmailInboxSettingsProps) {
	const [dialogOpen, setDialogOpen] = useState(false)

	const addresses = [...(workspace.emailSettings ?? [])].sort((a, b) => {
		if (a.isPrimary !== b.isPrimary) return a.isPrimary ? -1 : 1
		return a.primaryEmail.localeCompare(b.primaryEmail)
	})

	const limitQuery = api.mail.emailAddressLimit.useQuery()
	const limit = limitQuery.data?.limit ?? 1
	const used = limitQuery.data?.used ?? addresses.length

	return (
		<SettingsContainer className="space-y-3 divide-y-0">
			<div className="flex items-start justify-between gap-3">
				<div>
					<div className="text-sm font-medium">Addresses</div>
					<div className="mt-0.5 text-xs text-secondary">
						Each address gets its own inbox. Replies go out from the address the
						customer wrote to.
					</div>
				</div>
				<Button
					Icon={PlusIcon}
					variant="outline"
					className="flex-none"
					onClick={() => setDialogOpen(true)}
				>
					Add address
				</Button>
			</div>

			<div className="flex flex-col gap-1.5">
				{addresses.map((address) => (
					<AddressRow key={address.id} address={address} />
				))}
			</div>

			<AddAddressDialog
				isOpen={dialogOpen}
				onOpenChange={setDialogOpen}
				addresses={addresses}
				limit={limit}
				used={used}
			/>
		</SettingsContainer>
	)
}
